import { useState, type ReactNode } from 'react';
import { Button } from './Button';
import { ConfirmDialog } from './Dialog';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './Sheet';

interface Props {
  title: string;
  description: string;
  dirty: boolean;
  saving: boolean;
  canSave: boolean;
  /** Grund einer Ablehnung, so wie der Server ihn nennt. */
  error?: string | null;
  /** Beschriftung des Absende-Knopfs, wenn „Speichern“ nicht trifft, etwa „Schritt anlegen“. */
  submitLabel?: string;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Ein Formular im Sheet neben seinem Kontext (§8). Der Aufrufer hängt das Sheet nur während des
 * Bearbeitens ein, so beginnt jedes Öffnen mit den gespeicherten Werten. Angefangene
 * Eingaben gehen weder durch Escape noch durch einen Klick daneben verloren; während des
 * Speicherns bleibt das Sheet offen, damit bei einer Ablehnung alles noch dasteht.
 */
export function FormSheet(props: Props) {
  const { dirty, saving, canSave, onSubmit, onClose } = props;
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Ohne Radix-Trigger fiele der Fokus beim Schließen auf die Seite; er gehört zurück auf den
  // Knopf, der das Sheet geöffnet hat.
  const [returnFocusTo] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmLeave(true);
    else onClose();
  };

  return (
    <>
      <Sheet open onOpenChange={(next) => !next && requestClose()}>
        <SheetContent
          onCloseAutoFocus={(event) => {
            if (!returnFocusTo?.isConnected) return;
            event.preventDefault();
            returnFocusTo.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle className="text-ink font-serif text-[22px] leading-tight">
              {props.title}
            </SheetTitle>
            <SheetDescription className="text-ink-soft mt-1 text-sm">
              {props.description}
            </SheetDescription>
          </SheetHeader>
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSave && !saving) onSubmit();
            }}
          >
            <SheetBody className="space-y-5">
              {props.children}
              {props.error && (
                <p
                  role="alert"
                  className="border-sealwax/30 bg-sealwax/5 text-ink rounded-md border p-3 text-sm"
                >
                  {props.error}
                </p>
              )}
            </SheetBody>
            <SheetFooter>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                disabled={saving}
                onClick={requestClose}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                variant="brand"
                className="min-h-11"
                disabled={!canSave}
                loading={saving}
              >
                {props.submitLabel ?? 'Speichern'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={(next) => !next && setConfirmLeave(false)}
        title="Eingaben verwerfen?"
        description="Die Änderung ist noch nicht gespeichert. Beim Schließen geht sie verloren."
        confirmLabel="Verwerfen"
        cancelLabel="Weiter bearbeiten"
        onConfirm={() => {
          setConfirmLeave(false);
          onClose();
        }}
      />
    </>
  );
}
